import type { Target } from 'src/models/TimerTarget';

import { css, html } from 'react-strict-dom';
import { Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { formatAsReadableDate } from 'src/util';

import MaybeLink from './MaybeLink';
import Text from './Text';
import TimerButton from './TimerButton';
import TimerCompanyLogo from './TimerCompanyLogo';

type Props = {
  target: Target;
  /** When the work was last stopped, shown alongside the reference. */
  loggedAt: string;
  /** What the entry was logged as, which a fresh timer has to repeat. */
  category: string | null;
};

/**
 * One entry in the timer's quick switch row, which starts a fresh timer against
 * work the user was recently on.
 */
export default function TimerRecentTask({ target, loggedAt, category }: Props) {
  const when = formatAsReadableDate(loggedAt);

  return (
    <html.div style={styles.item}>
      <TimerCompanyLogo company={target.company} title={target.title} />

      <html.div style={styles.label}>
        <MaybeLink to={target.path} fill>
          <html.span style={styles.title}>
            <Text typeface="body.small.medium" color="neutral100">
              {target.title}
            </Text>
          </html.span>
        </MaybeLink>

        {/* The play button is the row's own action, so only the ticket text
            links out — and it stays outside the button. */}
        <html.span style={[Style.row, Style.alignCenter, styles.meta]}>
          {target.reference && (
            <MaybeLink to={target.path}>
              <Text typeface="caption.medium" color="neutral60">
                {target.reference}
              </Text>
            </MaybeLink>
          )}
          <Text typeface="caption.medium" color="neutral60" style={styles.when}>
            {target.reference ? `· ${when}` : when}
          </Text>
        </html.span>
      </html.div>

      <TimerButton target={target} category={category} />
    </html.div>
  );
}

const styles = css.create({
  item: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
    flexGrow: 1,
    backgroundColor: color.neutral5,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    paddingBlock: 8,
    paddingInline: 10,
    // Entries share the row evenly, then go full width once stacked.
    flexBasis: {
      default: 0,
      '@media (max-width: 640px)': 'auto',
    },
    alignSelf: {
      default: 'auto',
      '@media (max-width: 640px)': 'stretch',
    },
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
  },
  title: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    gap: 4,
    minWidth: 0,
  },
  when: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});
